import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from './Logo';

const GlobalNav = () => {
  const { user, logoutUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const timeoutRef = useRef(null);

  const handleLogout = () => {
    logoutUser();
    navigate('/');
    setMobileMenuOpen(false);
  };

  // Navigation items with dropdown children - exact order as specified
  const navLinks = [
    { 
      name: 'Business Setup', 
      path: '/pricing?category=business-setup',
      children: [
        { name: 'Sole Proprietorship', path: '/service/proprietorship' },
        { name: 'Private Limited Company', path: '/service/private-limited' },
        { name: 'Limited Liability Partnership (LLP)', path: '/service/llp' },
        { name: 'One Person Company (OPC)', path: '/service/opc' },
        { name: 'Partnership Firm', path: '/service/partnership' },
        { name: 'Section 8 Company', path: '/service/section-8' },
      ]
    },
    { 
      name: 'Licenses', 
      path: '/pricing?category=licenses',
      children: [
        { name: 'FSSAI Registration', path: '/service/fssai-registration' },
        { name: 'FSSAI Renewal', path: '/service/fssai-renewal' },
        { name: 'IEC (Import Export Code)', path: '/service/iec' },
        { name: 'MSME / Udyam Registration', path: '/service/msme' },
        { name: 'BIS Certification', path: '/service/bis' },
        { name: 'ISO Certification', path: '/service/iso' },
      ]
    },
    { 
      name: 'Trademark', 
      path: '/pricing?category=trademark',
      children: [
        { name: 'Trademark Registration', path: '/service/trademark-registration' },
        { name: 'Trademark Objection / Reply', path: '/service/trademark-objection' },
        { name: 'Trademark Renewal', path: '/service/trademark-renewal' },
        { name: 'Copyright Registration', path: '/service/copyright-registration' },
      ]
    },
    { 
      name: 'GST', 
      path: '/pricing?category=gst',
      children: [
        { name: 'GST Registration', path: '/service/gst-registration' },
        { name: 'Monthly GST Filing', path: '/service/gst-return' },
        { name: 'GST Modification', path: '/service/gst-modification' },
        { name: 'GST Cancellation', path: '/service/gst-cancellation' },
      ]
    },
    { 
      name: 'Tax & Payroll', 
      path: '/pricing?category=tax-payroll',
      children: [
        { name: 'Individual ITR Filing', path: '/service/itr-efiling' },
        { name: 'Business ITR Filing', path: '/service/business-tax' },
        { name: 'TDS Return Filing', path: '/service/tds-return' },
        { name: 'Income Tax Notice Reply', path: '/service/it-notice' },
        { name: 'PF (EPF) Registration', path: '/service/pf-registration' },
        { name: 'ESIC Registration', path: '/service/esi-registration' },
        { name: 'PF / ESIC Return Filing', path: '/service/pf-esi-return' },
      ]
    },
    { name: 'All Services', path: '/pricing' },
  ];

  // Check if current route is active or belongs to nav group
  const isActive = (link) => {
    if (location.pathname === link.path) return true;
    if (location.pathname + location.search === link.path) return true;
    if (link.children) {
      return link.children.some(child => 
        location.pathname === child.path ||
        location.pathname + location.search === child.path
      );
    }
    return false;
  };

  // Handle dropdown open/close with delay for better UX
  const handleMouseEnter = (name) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  // Toggle dropdown on click (for accessibility)
  const handleDropdownToggle = (e, name, hasChildren) => {
    if (hasChildren) {
      e.preventDefault();
      setOpenDropdown(openDropdown === name ? null : name);
    }
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e, name, hasChildren) => {
    if (hasChildren) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpenDropdown(openDropdown === name ? null : name);
      } else if (e.key === 'Escape') {
        setOpenDropdown(null);
      } else if (e.key === 'ArrowDown' && openDropdown === name) {
        e.preventDefault();
        const firstItem = dropdownRefs.current[name]?.querySelector('[role="menuitem"]');
        firstItem?.focus();
      }
    }
  }, [openDropdown]);

  // Handle menu item keyboard navigation
  const handleMenuItemKeyDown = (e, index, items, parentName) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (index + 1) % items.length;
      dropdownRefs.current[parentName]?.querySelectorAll('[role="menuitem"]')[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (index - 1 + items.length) % items.length;
      dropdownRefs.current[parentName]?.querySelectorAll('[role="menuitem"]')[prevIndex]?.focus();
    } else if (e.key === 'Escape') {
      setOpenDropdown(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdown && !e.target.closest('[data-dropdown]')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpenDropdown(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav 
      className="relative z-50 mx-auto max-w-5xl rounded-full border border-slate-200/60 bg-white/95 backdrop-blur-md px-6 py-3 shadow-sm" 
      data-testid="global-nav"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-between">
        {/* Logo */}
        <Logo size="default" linkTo="/" />
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-0.5">
          {navLinks.map((link) => {
            const hasChildren = link.children && link.children.length > 0;
            const isOpen = openDropdown === link.name;
            const active = isActive(link);

            return (
              <div 
                key={link.name}
                className="relative"
                data-dropdown
                onMouseEnter={() => hasChildren && handleMouseEnter(link.name)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Nav Item */}
                {hasChildren ? (
                  <button
                    className={`group relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                      active ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={(e) => handleDropdownToggle(e, link.name, hasChildren)}
                    onKeyDown={(e) => handleKeyDown(e, link.name, hasChildren)}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="relative">
                      {link.name}
                      {/* Underline indicator */}
                      <span 
                        className={`absolute -bottom-1 left-0 h-0.5 bg-slate-900 transition-all duration-200 ease-out ${
                          active ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      />
                    </span>
                    {/* Chevron */}
                    <ChevronDown 
                      className={`h-3.5 w-3.5 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  <Link
                    to={link.path}
                    className={`group relative flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                      active ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="relative">
                      {link.name}
                      {/* Underline indicator */}
                      <span 
                        className={`absolute -bottom-1 left-0 h-0.5 bg-slate-900 transition-all duration-200 ease-out ${
                          active ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      />
                    </span>
                  </Link>
                )}

                {/* Dropdown Menu */}
                {hasChildren && (
                  <div 
                    ref={el => dropdownRefs.current[link.name] = el}
                    className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 py-2 min-w-[220px] bg-white rounded-xl border border-slate-200 shadow-lg transition-all duration-200 ${
                      isOpen 
                        ? 'opacity-100 visible translate-y-0' 
                        : 'opacity-0 invisible -translate-y-2'
                    }`}
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.children.map((child, index) => (
                      <Link
                        key={child.path + index}
                        to={child.path}
                        className="block px-4 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                        role="menuitem"
                        tabIndex={isOpen ? 0 : -1}
                        onKeyDown={(e) => handleMenuItemKeyDown(e, index, link.children, link.name)}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Desktop Auth - Only show for logged-in users (admin access) */}
        <div className="hidden md:flex items-center gap-3">
          {user && (
            <>
              <Link to={user.is_admin ? '/admin' : '/dashboard'}>
                <Button variant="ghost" size="sm" data-testid="nav-dashboard">
                  {user.is_admin ? 'Admin' : 'Dashboard'}
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                data-testid="nav-logout"
              >
                Logout
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 -mr-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? (
            <X className="h-5 w-5 text-slate-600" />
          ) : (
            <Menu className="h-5 w-5 text-slate-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu - Full screen overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop overlay to block page content */}
          <div 
            className="md:hidden fixed inset-0 bg-white z-40"
            style={{ top: '0' }}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          
          {/* Mobile menu content */}
          <div 
            className="md:hidden fixed inset-x-0 top-0 z-50 bg-white min-h-screen overflow-y-auto"
            data-testid="mobile-menu"
          >
            {/* Mobile header with logo and close */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <Logo size="default" linkTo="/" />
              <button
                className="p-2 -mr-2"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </div>
            
            {/* Menu items */}
            <div className="px-6 py-4">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => {
                  const hasChildren = link.children && link.children.length > 0;
                  const isOpen = mobileOpenDropdown === link.name;
                  const active = isActive(link);

                  return (
                    <div key={link.name}>
                      {hasChildren ? (
                        <>
                          <button
                            className={`flex items-center justify-between w-full text-sm font-medium py-3 px-3 rounded-lg transition-colors ${
                              active
                                ? 'text-slate-900 bg-slate-50'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                            }`}
                            onClick={() => setMobileOpenDropdown(isOpen ? null : link.name)}
                            aria-expanded={isOpen}
                            aria-haspopup="true"
                          >
                            <span className="flex items-center gap-2">
                              {link.name}
                              {active && <span className="h-1.5 w-1.5 bg-slate-900 rounded-full" />}
                            </span>
                            <ChevronDown 
                              className={`h-4 w-4 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          {isOpen && (
                            <div className="ml-4 mt-1 mb-2 border-l-2 border-slate-200 pl-4 py-1 bg-slate-50/50 rounded-r-lg">
                              {link.children.map((child, idx) => (
                                <Link
                                  key={child.path + idx}
                                  to={child.path}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="block py-2.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                                >
                                  {child.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={link.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-2 text-sm font-medium py-3 px-3 rounded-lg transition-colors ${
                            active
                              ? 'text-slate-900 bg-slate-50'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                          }`}
                        >
                          {link.name}
                          {active && <span className="h-1.5 w-1.5 bg-slate-900 rounded-full" />}
                        </Link>
                      )}
                    </div>
                  );
                })}
                
                {/* Auth section - Only show for logged-in users (admin access) */}
                {user && (
                  <div className="pt-4 mt-4 border-t border-slate-200">
                    <div className="flex flex-col gap-2">
                      <Link to={user.is_admin ? '/admin' : '/dashboard'} onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" size="sm" className="w-full justify-start">
                          {user.is_admin ? 'Admin Panel' : 'Dashboard'}
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleLogout}
                        className="w-full"
                      >
                        Logout
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default GlobalNav;
